/**
 * AVIS GOOGLE — remplit les sections d'avis avec les vrais avis.
 *
 * Les avis viennent de l'app Reputon (Google Reviews), installée sur la
 * boutique : son adresse publique renvoie la note, le nombre d'avis, une
 * sélection d'avis et un résumé. On n'affiche pas son widget : on lit ses
 * données et on les pose dans notre propre markup —
 *   - data-avis-google="accueil" : citations (sections/home-reviews.liquid) ;
 *   - data-avis-google="produit" : synthèse + cartes paginées
 *     (sections/product-reviews.liquid) ;
 *   - data-avis-google="ligne" : une ligne de texte où [note] et [nombre]
 *     sont remplacés (preuve de l'accueil, note sous le titre produit).
 *     Elle porte .avis-attente — invisible, place gardée — jusqu'à ce que
 *     les chiffres arrivent ; sans données, elle le reste.
 *
 * L'adresse n'est pas documentée par Reputon. Si elle ne répond plus ou
 * change de forme, la section se retire d'elle-même plutôt que d'afficher
 * un cadre vide.
 *
 * Les données partent quand la section approche de l'écran, pas au
 * chargement de la page. Tout le texte est posé en `textContent` : un avis
 * ne peut pas injecter de HTML.
 */

let requete = null;

/* Une seule requête par page, même avec deux sections. */
function charger(url) {
  requete ??= fetch(String(url).trim())
    .then((reponse) => {
      if (!reponse.ok) throw new Error(reponse.status);
      return reponse.json();
    })
    .then((donnees) => {
      const fiche = donnees?.business?.[0];
      if (!fiche || typeof fiche.rating !== 'number') throw new Error('format inattendu');
      return fiche;
    });
  return requete;
}

const el = (balise, classe, texte) => {
  const n = document.createElement(balise);
  if (classe) n.className = classe;
  if (texte !== undefined) n.textContent = texte;
  return n;
};

const nombre = (n, decimales = 0) =>
  n.toLocaleString('fr-FR', { minimumFractionDigits: decimales, maximumFractionDigits: decimales });

const etoiles = (note) => '★'.repeat(Math.max(0, Math.min(5, Math.round(note))));

const lienGoogle = (fiche) =>
  fiche.placeId ? `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(fiche.placeId)}` : '';

/* Les avis publiables : non masqués par l'app, avec un texte. */
const publiables = (fiche) =>
  (fiche.reviews || []).filter((a) => !a.hide && typeof a.text === 'string' && a.text.trim());

const tronquer = (texte, max) => {
  const t = texte.replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max).replace(/\s+\S*$/, '')}…` : t;
};

function textes(section) {
  try {
    return JSON.parse(section.querySelector('[data-avis-textes]')?.textContent || '{}');
  } catch {
    return {};
  }
}

/* ── ligne : [note] et [nombre] remplacés dans le texte ─────────────
   On ne touche qu'aux nœuds de texte : la mise en forme saisie dans
   l'éditeur (gras, lien) reste intacte. */
function remplirLigne(ligne, fiche) {
  const valeurs = { '[note]': nombre(fiche.rating, 1), '[nombre]': nombre(fiche.reviewsNumber) };
  const parcours = document.createTreeWalker(ligne, NodeFilter.SHOW_TEXT);
  for (let n = parcours.nextNode(); n; n = parcours.nextNode()) {
    n.textContent = n.textContent.replace(/\[note\]|\[nombre\]/g, (jeton) => valeurs[jeton]);
  }
  ligne.classList.remove('avis-attente');
}

/* ── accueil : la ligne de note et quelques citations ─────────────── */
function remplirAccueil(section, fiche, T) {
  const ligne = section.querySelector('[data-avis-note]');
  if (ligne) {
    ligne.replaceChildren();
    const vstar = el('span', 'vstar', etoiles(fiche.rating));
    vstar.setAttribute('aria-hidden', 'true');
    ligne.append(vstar, ' ', el('b', '', `${nombre(fiche.rating, 1)}/5`), ' · ');
    const compte = `${nombre(fiche.reviewsNumber)} ${T.compte || ''}`.trim();
    const url = lienGoogle(fiche);
    if (url) {
      const a = el('a', 'hm-gmap');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener';
      const icone = section.querySelector('template[data-avis-icone]');
      if (icone) a.append(icone.content.cloneNode(true));
      a.append(compte);
      ligne.append(a);
    } else {
      ligne.append(compte);
    }
    ligne.hidden = false;
  }

  /* Des citations qui se lisent d'un coup d'œil : d'abord celles de
     longueur moyenne, les plus longues raccourcies si on en manque. */
  const max = Number(section.dataset.max) || 3;
  const avis = publiables(fiche);
  const choisis = [
    ...avis.filter((a) => a.text.length >= 40 && a.text.length <= 260),
    ...avis.filter((a) => a.text.length > 260),
  ].slice(0, max);

  const liste = section.querySelector('[data-avis-liste]');
  liste.replaceChildren(
    ...choisis.map((a) => {
      const bloc = el('blockquote', 'hm-quote');
      bloc.append(el('p', '', `« ${tronquer(a.text, 260)} »`));
      bloc.append(el('footer', '', [a.authorName, T.signature].filter(Boolean).join(' · ')));
      return bloc;
    })
  );
}

/* ── fiche produit : synthèse, résumé, cartes paginées ───────────────── */
function remplirProduit(section, fiche, T) {
  const poser = (selecteur, texte) => {
    const n = section.querySelector(selecteur);
    if (n) {
      n.textContent = texte;
      n.hidden = false;
    }
  };
  poser('[data-avis-score]', nombre(fiche.rating, 1));
  poser('[data-avis-etoiles]', etoiles(fiche.rating));
  poser('[data-avis-compte]', `${nombre(fiche.reviewsNumber)} ${T.compte || ''}`.trim());

  const voir = section.querySelector('[data-avis-voir]');
  if (voir && lienGoogle(fiche)) {
    voir.href = lienGoogle(fiche);
    voir.hidden = false;
  }
  const ecrire = section.querySelector('[data-avis-ecrire]');
  if (ecrire && fiche.writeReviewUrl) {
    ecrire.href = fiche.writeReviewUrl;
    ecrire.hidden = false;
  }

  const resume = (fiche.summary?.items || []).map((x) => String(x).replace(/[;.]\s*$/, '').trim()).filter(Boolean);
  const encadre = section.querySelector('[data-avis-resume]');
  if (encadre && resume.length) {
    /* Les points arrivent en phrases séparées (« Livraison rapide; ») :
       on les relie en une seule, sans majuscule au milieu. */
    const phrase = resume.map((x, i) => (i === 0 ? x : x.charAt(0).toLowerCase() + x.slice(1))).join(', ');
    encadre.querySelector('[data-avis-resume-texte]').textContent = `${phrase}.`;
    encadre.hidden = false;
  }

  const grille = section.querySelector('[data-avis-liste]');
  const cartes = publiables(fiche).map((a) => {
    const carte = el('article', 'rv-card');
    const tete = el('header');
    const vstar = el('span', 'vstar', etoiles(a.rating));
    vstar.setAttribute('aria-label', (T.etoiles || '[n] étoiles sur 5').replace('[n]', a.rating));
    tete.append(vstar);
    if (a.relativeTimeDescription) tete.append(el('time', '', a.relativeTimeDescription));
    const qui = el('p', 'rv-who', a.authorName || '');
    if (T.signature) qui.append(' · ', el('span', '', T.signature));
    carte.append(tete, qui, el('p', 'rv-txt', a.text.trim()));
    return carte;
  });
  grille.replaceChildren(...cartes);
  paginer(section, cartes);
}

function paginer(section, cartes) {
  const parPage = Number(section.dataset.parPage) || 6;
  const pager = section.querySelector('[data-avis-pager]');
  if (!pager || cartes.length <= parPage) return;

  const pages = Math.ceil(cartes.length / parPage);
  const compte = pager.querySelector('[data-avis-page]');
  const prev = pager.querySelector('[data-avis-prev]');
  const next = pager.querySelector('[data-avis-next]');
  let page = 0;

  const afficher = () => {
    cartes.forEach((carte, rang) => {
      carte.hidden = Math.floor(rang / parPage) !== page;
    });
    compte.textContent = `${page + 1} / ${pages}`;
    prev.disabled = page === 0;
    next.disabled = page === pages - 1;
  };
  prev.onclick = () => {
    if (page > 0) {
      page -= 1;
      afficher();
    }
  };
  next.onclick = () => {
    if (page < pages - 1) {
      page += 1;
      afficher();
    }
  };
  pager.hidden = false;
  afficher();
}

function monter(section) {
  if (section.dataset.avisMonte) return;
  section.dataset.avisMonte = '1';

  const lancer = () =>
    charger(section.dataset.source)
      .then((fiche) => {
        const T = textes(section);
        const mode = section.dataset.avisGoogle;
        if (mode === 'ligne') remplirLigne(section, fiche);
        else if (mode === 'produit') remplirProduit(section, fiche, T);
        else remplirAccueil(section, fiche, T);
        section.classList.add('est-charge');
      })
      .catch(() => {
        /* Pas de données : la section se retire plutôt que de rester vide.
           Une ligne garde sa place, invisible : la retirer ferait sauter
           ce qui la suit. */
        if (section.dataset.avisGoogle !== 'ligne') section.hidden = true;
      });

  if (!('IntersectionObserver' in window)) {
    lancer();
    return;
  }
  const veille = new IntersectionObserver(
    (entrees) => {
      if (entrees.some((e) => e.isIntersecting)) {
        veille.disconnect();
        lancer();
      }
    },
    { rootMargin: '800px 0px' }
  );
  veille.observe(section);
}

document.querySelectorAll('[data-avis-google]').forEach(monter);
document.addEventListener('shopify:section:load', (evenement) => {
  evenement.target.querySelectorAll('[data-avis-google]').forEach(monter);
});
